import { useState } from 'react'
import { ArrowRight, X } from 'lucide-react'
import {
  Board,
  BoardCard,
  BoardColumn,
  CardFieldKey,
  cardTitle,
  fieldDef,
  fieldsNeededFor,
  isFieldFilled,
} from '../../lib/boards'
import { CardFieldInput, TutorOption, inputClass, labelClass } from './CardFieldInput'
import { columnAccent, ghostButton, iconButton, overlayClass, panelClass, primaryButton } from './ui'

interface MoveCardModalProps {
  card: BoardCard
  target: BoardColumn
  boards: Board[]
  columns: BoardColumn[]
  tutors: TutorOption[]
  missing: CardFieldKey[]
  onCancel: () => void
  onConfirm: (patch: Partial<BoardCard>) => Promise<void>
}

export function MoveCardModal({ card, target, boards, columns, tutors, missing, onCancel, onConfirm }: MoveCardModalProps) {
  const [values, setValues] = useState<Partial<BoardCard>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const merged = { ...card, ...values }
  const needed = fieldsNeededFor(target, columns.filter(c => c.boardId === target.boardId))
  const stillMissing = needed.filter(f => !isFieldFilled(merged, f))
  const needsOtherReason = target.entryFields.includes('closeReason') && merged.closeReason === 'other'
  const otherReasonMissing = needsOtherReason && !merged.closeReasonOther?.trim()

  const handleSubmit = async () => {
    if (stillMissing.length > 0 || otherReasonMissing) {
      setError('Fill in every required field to move this card.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onConfirm(values)
    } catch (e: any) {
      setError(e?.message || 'Could not move the card.')
      setSaving(false)
    }
  }

  const from = columns.find(c => c.id === card.columnId)
  const targetBoardKey = boards.find(b => b.id === target.boardId)?.key
  const boardName = (boardId: string) => (target.boardId !== card.boardId ? boards.find(b => b.id === boardId)?.name : undefined)

  return (
    <div className={`${overlayClass} z-[60]`}>
      <div className={`${panelClass} w-full max-w-md max-h-[90vh] flex flex-col`} role="dialog" aria-modal="true" aria-label={`Move to ${target.name}`}>
        <div className="flex items-start justify-between gap-4 p-6 pb-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{cardTitle(card)}</h2>
            <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs font-medium">
              {from && <ColumnChip column={from} board={boardName(from.boardId)} />}
              <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
              <ColumnChip column={target} board={boardName(target.boardId)} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {target.kind === 'parked'
                ? 'A reason is needed for this column.'
                : 'These details are needed before the card can move on.'}
            </p>
          </div>
          <button onClick={onCancel} aria-label="Close" className={iconButton}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-1 space-y-4 overflow-y-auto">
          {missing.map(key => (
            <CardFieldInput
              key={key}
              def={fieldDef(key, targetBoardKey)}
              value={(merged as any)[key]}
              onChange={(v) => setValues(prev => ({ ...prev, [key]: v }))}
              tutors={tutors}
              required
            />
          ))}
          {needsOtherReason && (
            <div>
              <label className={labelClass}>
                Other reason<span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                value={merged.closeReasonOther ?? ''}
                onChange={(e) => setValues(prev => ({ ...prev, closeReasonOther: e.target.value }))}
                className={inputClass}
              />
            </div>
          )}
        </div>

        {error && <p className="px-6 mt-4 text-sm text-red-500">{error}</p>}

        <div className="flex justify-end gap-2 mt-6 px-6 py-4 border-t border-gray-100 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.02] rounded-b-2xl">
          <button onClick={onCancel} className={ghostButton}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving} className={primaryButton}>
            {saving ? 'Moving…' : 'Save and move'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ColumnChip({ column, board }: { column: BoardColumn; board?: string }) {
  const accent = columnAccent(column)
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md ${accent.soft}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
      {board ? `${board} · ${column.name}` : column.name}
    </span>
  )
}
